import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Images
import CatchingOnPosters from '@/media/images/EllieHoldingLaptopHomepage.jpeg';
import CatchingOnLaptop from '@/media/images/EllieHeadshot_HomePage.jpeg';

const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
};

export const Home = () => {
    return (
        <div data-testid="home-page">
            {/* Hero Section */}
            <section className="bg-brand-periwinkle pt-32 pb-20 px-6 md:px-12" data-testid="hero-section">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-black leading-none mb-8">
                                MARKETING<br />
                                THAT FINALLY<br />
                                <span className="inline-block bg-brand-lime px-2">CLICKS</span>
                            </h1>
                            <p className="text-xl text-black/80 max-w-md mb-10 leading-relaxed">
                                At Catching On, we believe marketing shouldn't feel confusing, 
                                overwhelming, or full of jargon.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact">
                                    <button 
                                        className="btn-lime"
                                        data-testid="hero-contact-btn"
                                    >
                                        CONTACT
                                    </button>
                                </Link>
                                <Link to="/services">
                                    <button 
                                        className="btn-lime"
                                        data-testid="hero-services-btn"
                                    >
                                        SERVICES
                                    </button>
                                </Link>
                            </div>
                        </motion.div>
                        
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="relative hidden lg:block"
                        >
                            <div className="full-arch aspect-[3/4] max-w-md ml-auto">
                                <img
                                    src={CatchingOnPosters}
                                    alt="Ellie of CatchingOn holding a laptop"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="py-24 bg-brand-periwinkle" data-testid="about-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <motion.div
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={fadeInUp}
                        className="grid lg:grid-cols-2 gap-16 items-center"
                    >
                        <div className="full-arch aspect-[3/4] max-w-sm">
                            <img
                                src={CatchingOnLaptop}
                                alt="Ellie, founder of CatchingOn"
                                className="w-full h-full object-cover object-[38%_center]"
                            />
                        </div>

                        <div>
                            <h2 className="text-5xl lg:text-6xl font-black text-black leading-none mb-8 uppercase">
                                <span className="bg-brand-lime px-2">LEARN</span> MORE<br />
                                ABOUT US
                            </h2>
                            <p className="text-lg text-black mb-6 leading-relaxed">
                                Established in 2024, we set out to help businesses to make sense of
                                their marketing and focus on what actually works.
                            </p>
                            <p className="text-lg text-black mb-6 leading-relaxed">
                                There's a lot of noise out there, new platforms, new trends, new
                                "must-do" tactics every week. The truth? Most businesses don't need
                                more marketing. They need <span className="bg-brand-lime px-1 font-bold">better marketing</span>.
                            </p>
                            <p className="text-lg text-black mb-8 leading-relaxed">
                                We take a straightforward, no-BS approach to marketing and
                                communication strategies.
                            </p>
                            <Link to="/about">
                                <button className="btn-lime" data-testid="learn-more-btn">
                                    ABOUT
                                </button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Feedback Section */}
            <section className="py-24 bg-brand-lime" data-testid="feedback-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <motion.div
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={fadeInUp}
                    >
                        <h2 className="text-5xl lg:text-6xl font-black text-black uppercase mb-3">FEEDBACK</h2>
                        <p className="text-lg text-black mb-12">
                            Don't just take our word for it — hear from the businesses we've helped along the way.
                        </p>
                        <div className="grid md:grid-cols-2 gap-6">
                            {[
                                { name: 'John Ellery',      company: 'Get Jobs',         review: 'At Get Jobs we were really fortunate to work with Ellie from Catching On. They have supported us to put in place clear processes that we otherwise would not have thought of. The team were really easy to work with, full of great ideas and understanding of our needs and preferences. We would recommend using Catching On to anyone needing some professional support.' },
                                { name: 'Cameron S',        company: 'Altorite LTD',     review: 'They have been very professional and informative about how we should handle our marketing. We have really appreciated the advice and service they provide!' },
                                { name: 'Gary Lockwood',    company: '24/7 Fitness',     review: 'Since working with Catching On we have seen an enormous improvement in our social and online presence, the team are always helpful and full of great ideas. I would highly recommend to any business who is looking to maximise their brand online and drive sales through digital channels.' },
                                { name: 'Honor H',          company: 'Thornspire Games', review: 'From the first call we had with Catching On they listened to everything we wanted for our socials and applied them seamlessly.' },
                            ].map((testimonial, i) => (
                                <div key={i} className="bg-white rounded-3xl p-8 flex flex-col gap-4">
                                    <div className="flex gap-1">
                                        {[...Array(5)].map((_, s) => (
                                            <span key={s} className="text-xl text-yellow-500">★</span>
                                        ))}
                                    </div>
                                    <p className="text-black text-sm leading-relaxed">{testimonial.review}</p>
                                    <div className="flex items-center gap-3 mt-2">
                                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-500">
                                            {testimonial.name[0]}
                                        </div>
                                        <div>
                                            <p className="font-bold text-black text-sm">{testimonial.name}</p>
                                            <p className="text-black/60 text-xs">{testimonial.company}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Home;
